"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import PageHeader from "./components/PageHeader";
import StatsSection from "./components/StatsSection";
import ReviewFilters from "./components/ReviewFilters";
import StoryFeed from "./components/ReviewFeed";
import MidPageCta from "./components/MidPageCta";
import BottomCta from "./components/BottomCta";
import { injectMetaTags } from "@/lib/seoHelpers";
import { stories } from "@/mocks/reviews";

export default function ReviewsPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();
  const [serviceFilter, setServiceFilter] = useState("All");
  const [neighborhoodFilter, setNeighborhoodFilter] = useState("All");
  const [photosOnly, setPhotosOnly] = useState(false);
  const [sortBy, setSortBy] = useState("Most Recent");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  const filteredStories = useMemo(() => {
    let result = [...stories];
    if (serviceFilter !== "All") {
      result = result.filter((s) => s.service === serviceFilter);
    }
    if (neighborhoodFilter !== "All") {
      result = result.filter((s) => s.neighborhood === neighborhoodFilter);
    }
    if (photosOnly) {
      result = result.filter((s) => s.hasPhotos);
    }
    return result;
  }, [serviceFilter, neighborhoodFilter, photosOnly]);

  const sortedStories = useMemo(() => {
    const result = [...filteredStories];
    if (sortBy === "Most Recent") {
      result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (sortBy === "With Photos") {
      result.sort(
        (a, b) =>
          (b.hasPhotos ? 1 : 0) - (a.hasPhotos ? 1 : 0) ||
          new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    }
    return result;
  }, [filteredStories, sortBy]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [serviceFilter, neighborhoodFilter, photosOnly, sortBy]);

  useEffect(() => {
    const isEs = currentLang === "es";
    const title = isEs
      ? "Historias de Clientes \u2014 Reparaci\u00F3n de Ventanas Miami"
      : "Customer Stories \u2014 Miami Window Repair";
    const description = isEs
      ? "Ejemplos reales de proyectos de reparaci\u00F3n de ventanas en Miami de casas de clientes en 12 vecindarios de Miami-Dade. Historias de puertas corredizas, vidrios, herrajes y burletes."
      : "Real Miami window repair project examples from customer homes across 12 Miami-Dade neighborhoods. Sliding door, glass, hardware, weatherstripping stories.";

    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", description);
    }

    const cleanupSeo = injectMetaTags({
      title,
      description,
      url: "https://impact-window-repair-miami.com/reviews/",
      image: "https://impact-window-repair-miami.com/og/og-stories.jpg",
      imageAlt: isEs ? "Historias de clientes Miami" : "Customer stories Miami",
      breadcrumbs: [
        { name: isEs ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: isEs ? "Historias de Clientes" : "Customer Stories", item: "https://impact-window-repair-miami.com/reviews/" }
      ],
      type: "website",
    });

    return () => {
      cleanupSeo();
    };
  }, [currentLang]);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    const isEs = currentLang === "es";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          name: isEs ? "Ejemplos de Proyectos de Reparaci\u00F3n" : "Window and Door Repair Example Projects",
          inLanguage: isEs ? "es-US" : "en-US",
          itemListElement: stories.map((s, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            item: {
              "@type": "Service",
              name: s.title,
              description: isEs ? s.bodyEs : s.bodyEn,
              areaServed: {
                "@type": "City",
                name: s.neighborhood,
              },
              provider: {
                "@type": "LocalBusiness",
                name: "Windows and Doors Repair LLC",
                telephone: "+17863774452",
              },
            },
          })),
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          url: "https://impact-window-repair-miami.com",
          geo: {
            "@type": "GeoCoordinates",
            "latitude": 25.7617,
            "longitude": -80.1918
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "07:00",
            "closes": "21:00"
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            {"@type": "City", "name": "Miami"},
            {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: isEs ? "Reparaci\u00F3n honesta de ventanas en Miami. El mismo d\u00EDa. Garant\u00EDa de 30 d\u00EDas." : "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          serviceType: [
            "Sliding Door Repair",
            "Glass Replacement",
            "Window Hardware Repair",
            "Weatherstripping & Sealing",
            "Window Cleaning & Maintenance",
            "Emergency Board-Up",
          ],
        },
      ],
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [currentLang]);

  const handleClear = () => {
    setServiceFilter("All");
    setNeighborhoodFilter("All");
    setPhotosOnly(false);
    setSortBy("Most Recent");
  };

  return (
    <div className="font-roboto">
      <PageHeader />
      <StatsSection />

      {/* Hero / Disclaimer */}
      <div className="bg-[#fefce8]">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 pt-10 pb-6 text-center">
          <p className="text-xs font-bold tracking-widest text-accent uppercase mb-3">
            {t("reviews.eyebrow")}
          </p>
          <h1 className="text-3xl md:text-[42px] font-black text-secondary leading-tight mb-4">
            {t("reviews.h1")}
          </h1>
          <p className="text-base md:text-lg text-[#374151] max-w-[720px] mx-auto mb-6">
            {t("reviews.subhead")}
          </p>
          <div className="bg-white border-l-4 border-accent rounded-lg px-4 py-4 max-w-[720px] mx-auto">
            <p className="text-sm italic text-stone-500">
              {t("reviews.disclaimer")}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex gap-10">
          {/* Sidebar Desktop */}
          <div className="hidden lg:block w-[280px] shrink-0">
            <div className="sticky top-20">
              <ReviewFilters
                serviceFilter={serviceFilter}
                setServiceFilter={setServiceFilter}
                neighborhoodFilter={neighborhoodFilter}
                setNeighborhoodFilter={setNeighborhoodFilter}
                photosOnly={photosOnly}
                setPhotosOnly={setPhotosOnly}
                onClear={handleClear}
              />
            </div>
          </div>

          {/* Mobile Drawer */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-secondary/50"
                onClick={() => setMobileFiltersOpen(false)}
              />
              <div className="absolute left-0 top-0 bottom-0 w-[280px] bg-white shadow-xl overflow-y-auto">
                <ReviewFilters
                  serviceFilter={serviceFilter}
                  setServiceFilter={setServiceFilter}
                  neighborhoodFilter={neighborhoodFilter}
                  setNeighborhoodFilter={setNeighborhoodFilter}
                  photosOnly={photosOnly}
                  setPhotosOnly={setPhotosOnly}
                  onClear={handleClear}
                  isMobile
                  onCloseMobile={() => setMobileFiltersOpen(false)}
                />
              </div>
            </div>
          )}

          {/* Feed */}
          <div ref={feedRef} className="flex-1 min-w-0">
            <StoryFeed
              stories={sortedStories}
              sortBy={sortBy}
              setSortBy={setSortBy}
              onOpenMobileFilters={() => setMobileFiltersOpen(true)}
            />
            <MidPageCta />
          </div>
        </div>
      </div>
      <BottomCta />
    </div>
  );
}